import { useDagschoothoogteHeight } from "app/hooks/configurator/useDagschoothoogteHeight";
import { useOpdekdeurHingeHeight } from "app/hooks/configurator/useOpdekdeurHingeHeight";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import { EigenwijsOptions } from "app/providers/DoorOptionsProvider.types";
import MeasurementOptionControls from "features/option-controls/measurements-option-controls";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SelfAssemblyMeasurements } from "shared/api/middleware-api-client";
import FormGroup from "shared/ui/FormGroup";
import Input from "shared/ui/input";
import Label from "shared/ui/label";
import ToggleButton from "widgets/shared/toggle/components/ToggleButton";
import { ToggleLayout } from "widgets/shared/toggle/components/ToggleLayout";
import RightOrientation from "shared/ui/icons/selfAssembly/RightOrientation";

interface Props {
  onShowInfoClick: ({
    title,
    description,
  }: {
    title: string;
    description: string;
  }) => void;
}
const OpdekdeurSelfInstallment = ({ onShowInfoClick }: Props) => {
  const [searchParams] = useSearchParams();

  const {
    eigenwijs,
    handle,
    updateSelfAssembleData,
    updateSearchParam,
    selfAssembleDoorData,
    updateAnchorBeams,
    updateEigenwijs,
  } = useConfigurationOptionsContext();
  const { getHingeHeight } = useOpdekdeurHingeHeight();
  const { getDagschoothoogteHeight } = useDagschoothoogteHeight();

  const [isDefaultDataInitialized, setIsDefaultDataInitialized] =
    useState(false);

  const [defaultEigenwijs, setDefaultEigenwijs] = useState<
    EigenwijsOptions | undefined
  >(undefined);

  const doorMeasurements = useMemo(() => {
    let width = 0;
    let height = 0;

    if (eigenwijs) {
      width = eigenwijs.sizeObjects[0].customWidth;
      height = eigenwijs.sizeObjects[0].customHeight;
    }

    return { width, height };
  }, [eigenwijs?.sizeObjects]);

  useEffect(() => {
    initializeDefaultOpdekdeurSelfInstallmentData();
  }, []);
  useEffect(() => {
    if (!isDefaultDataInitialized) return;
    setDoorMeasurements(doorMeasurements);
  }, [isDefaultDataInitialized, doorMeasurements]);
  useEffect(() => {
    if (
      !isDefaultDataInitialized ||
      !selfAssembleDoorData.measurements.hingeAmount
    )
      return;
    setHinges(selfAssembleDoorData.measurements.hingeAmount);
  }, [doorMeasurements.height]);

  const initializeDefaultOpdekdeurSelfInstallmentData = () => {
    const data = { ...selfAssembleDoorData };

    data.measurements = {
      rotation: "left", // Draairichting left|right
      frameType: "steel", // Type kozijn
      hingeAmount: 3, // Aantal scharnieren
      easyToReach: false, //Default
      deliverInParts: false, //Default
      onLocation: false, //Default
      extraFloor: true, //Default
      scheluwHangTop: 0, //Default
      scheluwHangBottom: 0, //Default
      scheluwSluitTop: 0, //Default
      scheluwSluitBottom: 0, //Default
      dagschoothoogte: getDagschoothoogteHeight(doorMeasurements.height), //Default,
      skews: {
        width: [],
        height: [],
        openHeight: [],
      },
    };

    data.measurements.hinge1 = getHingeHeight(doorMeasurements.height, 3, 0);
    data.measurements.hinge2 = getHingeHeight(doorMeasurements.height, 3, 1);
    data.measurements.hinge3 = getHingeHeight(doorMeasurements.height, 3, 2);

    if (searchParams.get("label")) data.label = searchParams.get("label") || "";
    if (searchParams.get("rotation"))
      data.measurements.rotation = searchParams.get("rotation") || "";
    if (searchParams.get("frameType"))
      data.measurements.frameType = searchParams.get("frameType") || "";
    if (searchParams.get("hingeAmount"))
      data.measurements.hingeAmount = parseInt(
        searchParams.get("hingeAmount") || "3",
      );

    updateSelfAssembleData(data);
    setIsDefaultDataInitialized(true);
  };

  const setDoorMeasurements = (doorMeasurements: {
    width: number;
    height: number;
  }) => {
    const data = { ...selfAssembleDoorData };
    data.measurements.dagschoothoogte = getDagschoothoogteHeight(
      doorMeasurements.height,
    );
    data.measurements.sponningsBreedte = doorMeasurements.width;
    data.measurements.sponningsHoogte = doorMeasurements.height;
    data.measurements.skews = {
      width: Array(4)
        .fill(0)
        .map(() => ({
          size: doorMeasurements.width,
          offset: 0,
        })),
      height: Array(3)
        .fill(0)
        .map(() => ({
          size: doorMeasurements.height,
          offset: 0,
        })),
      openHeight: [
        [],
        [],
        [],
        [
          {
            offset: 0, //Default
          },
          {
            offset: 0, //Default
          },
        ],
      ],
    };

    updateSearchParam("sponningsBreedte", String(doorMeasurements.width));
    updateSearchParam("sponningsHoogte", String(doorMeasurements.height));
    updateSelfAssembleData(data);
  };

  const setLabel = (label: string) => {
    let data = { ...selfAssembleDoorData };
    data.label = label;

    updateSearchParam("label", label);
    updateSelfAssembleData(data);
  };

  const setRotationDirection = (direction: string) => {
    let data = { ...selfAssembleDoorData };
    data.measurements.rotation = direction;

    updateSearchParam("rotation", direction);
    updateSelfAssembleData(data);
  };

  const setFrameType = (type: string) => {
    let data = { ...selfAssembleDoorData };
    data.measurements.frameType = type;

    updateSearchParam("frameType", type);
    updateSelfAssembleData(data);

    // Remove u-greep if the change is from steel -> wood
    if (type === "wood" && handle?.code === "verticalhandle") {
      updateSearchParam("handle", "");
    }
  };

  const setHinges = (amount: number) => {
    let data = { ...selfAssembleDoorData };
    data.measurements.hingeAmount = amount;

    data.measurements.hinge1 = getHingeHeight(
      doorMeasurements.height,
      amount,
      0,
    );
    data.measurements.hinge2 = getHingeHeight(
      doorMeasurements.height,
      amount,
      1,
    );
    if (amount === 3) {
      data.measurements.hinge3 = getHingeHeight(
        doorMeasurements.height,
        amount,
        2,
      );
      setDoorModelHinges(3);
    } else {
      delete data.measurements.hinge3;
      setDoorModelHinges(2);
    }

    updateSearchParam("hingeAmount", amount.toString());
    updateSelfAssembleData(data);
  };

  const setDoorModelHinges = (amount: number) => {
    if (amount === 2) {
      if (eigenwijs && eigenwijs.beams.length > 2) {
        const newEigenwijs = { ...eigenwijs };
        const newBeams = [...newEigenwijs.beams];
        newBeams.splice(1, 1);

        newEigenwijs.beams = updateAnchorBeams(
          doorMeasurements.height,
          newBeams,
        );

        setDefaultEigenwijs(eigenwijs);
        updateEigenwijs(newEigenwijs);
      }
    } else if (amount === 3) {
      if (defaultEigenwijs) updateEigenwijs(defaultEigenwijs);
    }
  };

  // Determine if the rotation direction is left
  const isRotationDirectionLeft =
    selfAssembleDoorData.measurements.rotation === "left";

  // Determine if the frame type is wood
  const isFrameWood = selfAssembleDoorData.measurements.frameType === "wood";

  // Determine if the frame type is steel
  const isFrameSteel = selfAssembleDoorData.measurements.frameType === "steel";

  // Determine if the hinge number is 2
  const isHingeNr2Active = selfAssembleDoorData.measurements.hingeAmount === 2;

  return (
    <div className="gg-flex gg-flex-col gg-gap-y-5">
      <FormGroup>
        <Label
          htmlFor="self-installment-label"
          className="gg-mb-2 gg-font-bold"
        >
          Label
        </Label>
        <Input
          placeholder={
            "Vul hier de ruimte in waar de deur komt, bijv. Woonkamer."
          }
          key={`self-installment-label`}
          className={
            "gg-border-dark-blue-200 gg-py-3 gg-pr-12 placeholder:gg-italic placeholder:gg-text-grey-200"
          }
          id={`self-installment-label`}
          data-testid={"self-installment-label"}
          type={"string"}
          value={selfAssembleDoorData.label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label
          htmlFor="rotation-direction"
          className="gg-mb-2 gg-font-bold"
          onInfoButtonClick={() =>
            onShowInfoClick({
              title: "Draairichting",
              description:
                "Om de draairichting te bepalen, ga je in de ruimte staan waar je de deur naar je toe moet trekken om 'm te openen. Een deur met scharnieren aan de linkerzijde is linksdraaiend. Een deur met scharnieren aan de rechterzijde is rechtsdraaiend.",
            })
          }
          showInfoButton
        >
          Draairichting
        </Label>

        <ToggleLayout>
          <ToggleButton
            isActive={isRotationDirectionLeft}
            onClick={() => setRotationDirection("left")}
            roundedClass="gg-rounded-l-sm gg-border-l gg-grid gg-place-items-center "
            layoutId="rotationDirectionToggle"
            dataTestId="rotationDirectionToggleLeft"
          >
            <RightOrientation
              className="gg-mb-2 gg-scale-x-[-1] gg-pt-2 sm:gg-mb-4"
              isActive={isRotationDirectionLeft}
            />
            Linksdraaiend
          </ToggleButton>

          <ToggleButton
            isActive={!isRotationDirectionLeft}
            onClick={() => setRotationDirection("right")}
            roundedClass="gg-rounded-r-sm gg-border-r gg-grid gg-place-items-center "
            layoutId="rotationDirectionToggle"
            dataTestId="rotationDirectionToggleRight"
          >
            <RightOrientation
              className="gg-mb-2 gg-pt-2 sm:gg-mb-4"
              isActive={!isRotationDirectionLeft}
            />
            Rechtsdraaiend
          </ToggleButton>
        </ToggleLayout>
      </FormGroup>

      <MeasurementOptionControls />

      <FormGroup>
        <Label htmlFor="frame" className="gg-mb-2 gg-font-bold">
          Materiaal bestaand kozijn
        </Label>

        <ToggleLayout>
          <ToggleButton
            isActive={isFrameSteel}
            onClick={() => setFrameType("steel")}
            roundedClass="gg-rounded-l-sm gg-border-l"
            layoutId="woodFrameToggle"
            dataTestId="woodFrameToggleStaal"
          >
            Staal
          </ToggleButton>
          <ToggleButton
            isActive={isFrameWood}
            onClick={() => setFrameType("wood")}
            roundedClass="gg-rounded-r-sm gg-border-r"
            layoutId="woodFrameToggle"
            dataTestId="woodFrameToggleWood"
          >
            Hout
          </ToggleButton>
        </ToggleLayout>
      </FormGroup>

      <FormGroup>
        <Label
          htmlFor="hinge"
          className="gg-mb-2 gg-font-bold"
          onInfoButtonClick={() =>
            onShowInfoClick({
              title: "Aantal scharnieren",
              description: "Vul hier het huidige aantal scharnieren in.",
            })
          }
          showInfoButton
        >
          Aantal scharnieren
        </Label>

        <ToggleLayout>
          <ToggleButton
            isActive={isHingeNr2Active}
            onClick={() => setHinges(2)}
            roundedClass="gg-rounded-l-sm gg-border-l"
            layoutId="hingeNumberToggle"
            dataTestId="hingeNumberToggle2"
          >
            2
          </ToggleButton>

          <ToggleButton
            isActive={!isHingeNr2Active}
            onClick={() => setHinges(3)}
            roundedClass="gg-rounded-r-sm gg-border-r"
            layoutId="hingeNumberToggle"
            dataTestId="hingeNumberToggle3"
          >
            3
          </ToggleButton>
        </ToggleLayout>
      </FormGroup>

      <FormGroup>
        <Label
          htmlFor="latch-height"
          className="gg-mb-2 gg-font-bold"
          onInfoButtonClick={() =>
            onShowInfoClick({
              title: "Dagschoothoogte",
              description:
                "Komt de standaard maat niet overeen met het dagschoothoogte van jouw eigen kozijn? Neem dan eerst contact met ons op.",
            })
          }
          showInfoButton
        >
          Dagschoothoogte
        </Label>
        <Input
          key={`latch-height`}
          className={"gg-border-dark-blue-200 gg-py-3 gg-pr-12"}
          id={`latch-height`}
          data-testid={"latch-height"}
          type={"number"}
          value={
            selfAssembleDoorData.measurements.dagschoothoogte?.toString() ||
            "1054"
          }
          readOnly
          suffix={"mm"}
        />
      </FormGroup>

      <FormGroup>
        <Label
          htmlFor="hinge-height"
          className="gg-mb-2 gg-font-bold"
          onInfoButtonClick={() =>
            onShowInfoClick({
              title: "Scharnierhoogte",
              description:
                "Komt de standaard maat niet overeen met de scharnierhoogte van jouw eigen kozijn? Neem dan eerst contact met ons op.",
            })
          }
          showInfoButton
        >
          Scharnierhoogte
        </Label>
        {selfAssembleDoorData.measurements &&
          selfAssembleDoorData.measurements.hingeAmount && (
            <ul className={`gg-grid gg-gap-y-2`}>
              {Array(selfAssembleDoorData.measurements.hingeAmount)
                .fill(0)
                .map((_, i) => (
                  <li key={`hinge-height-${i}`}>
                    <Input
                      className={`gg-py-3`}
                      id={`hinge-height-${i}`}
                      data-testid={`hinge-height-${i}`}
                      type={"number"}
                      value={selfAssembleDoorData.measurements[
                        `hinge${i + 1}` as keyof SelfAssemblyMeasurements
                      ]?.toString()}
                      readOnly
                      suffix={"mm"}
                      error={
                        selfAssembleDoorData.measurements[
                          `hinge${i + 1}` as keyof SelfAssemblyMeasurements
                        ] === 0
                      }
                      errorText={
                        "Let op: vanaf een hoogte van 2116mm hebben deuren 3 scharnieren nodig. Wijkt jouw kozijn af? Neem contact met ons op."
                      }
                    />
                  </li>
                ))}
            </ul>
          )}
      </FormGroup>
    </div>
  );
};
export default OpdekdeurSelfInstallment;
